import React, { ReactNode } from 'react';
import { translate } from 'translations/i18n';
import { Spinner, Billboard, Text } from '@companycam/slab-web';
import { formatDate } from 'helpers/intl-helper';

import TemplateRow from 'components/projects/TemplateRow';
import type { TemplateAndExampleObject } from 'components/projects/UseTemplateModal';
import styled from 'styled-components';

const SectionDivider = styled.div`
  background: var(--Color-Surface-2-Base, #f5f7fa);
  padding: 6px var(--cc_size_spacing_m) 6px var(--cc_size_spacing_m);
  margin-bottom: var(--cc_size_spacing_m);
  gap: 10px;
`;

const Row = styled.div`
  display: flex;
  justify-content: center;
`;

const formatLastUsedAtDate = (lastUsedAt: string) => {
  if (lastUsedAt) {
    const date = new Date(lastUsedAt);
    return formatDate(date, "M/dd/yy 'at' h:mm a");
  }

  return null;
};

type UseTemplateModalBodyProps<T> = {
  isLoading: boolean;
  templates: T[];
  recentTemplates: T[];
  onUseTemplate: (id: number | string) => void;
  onPreview: (id: number | string) => void;
  onEdit: (id: number | string) => void;
  onDelete: (id: number | string) => void;
  emptyState: ReactNode;
  isError: boolean;
};

const UseTemplateModalBody = <T extends TemplateAndExampleObject>({
  isLoading,
  templates,
  recentTemplates,
  onUseTemplate,
  onPreview,
  onEdit,
  onDelete,
  emptyState,
  isError,
}: UseTemplateModalBodyProps<T>) => {
  if (isLoading) {
    return (
      <Row style={{ marginTop: 40 }}>
        <Spinner
          size="large"
          accessibilityLabel="loading_page_templates_spinner"
        />
      </Row>
    );
  }

  if (isError) {
    return (
      <Billboard
        color="destroy"
        iconName="alert-circle-outline"
        message={translate('useTemplateModal.getTemplatesErrorMessage')}
        title={translate('common.error')}
      />
    );
  }

  if (templates.length === 0) {
    return emptyState;
  }

  return (
    <>
      <SectionDivider>
        <Text as="p" weight="600" color="subtle">
          {translate('common.recentlyUsed').toLocaleUpperCase()}
        </Text>
      </SectionDivider>

      {recentTemplates.length === 0 ? (
        <Row style={{ marginBottom: 20 }}>
          <Text as="p" weight="600" color="subtle">
            {translate('templates.shared.noRecentlyUsedTemplates')}
          </Text>
        </Row>
      ) : null}

      {recentTemplates.map((template) => (
        <TemplateRow
          key={template.id}
          title={
            template.title ||
            translate('projectPage.projectPageList.emptyTitle')
          }
          isExample={template.isExample}
          description={template.description}
          lastUsedAt={formatLastUsedAtDate(template.lastUsedAt)}
          onClick={() => onUseTemplate(template.id)}
          onPreviewClick={() => onPreview(template.id)}
          onEditClick={() => onEdit(template.id)}
          onDeleteClick={() => onDelete(template.id)}
        />
      ))}

      <SectionDivider>
        <Text as="p" weight="600" color="subtle">
          {translate('common.all').toUpperCase()}
        </Text>
      </SectionDivider>

      {templates.map((template) => (
        <TemplateRow
          key={template.id}
          title={
            template.title ||
            translate('projectPage.projectPageList.emptyTitle')
          }
          description={template.description}
          isExample={template.isExample}
          lastUsedAt={formatLastUsedAtDate(template.lastUsedAt)}
          onClick={() => onUseTemplate(template.id)}
          onPreviewClick={() => onPreview(template.id)}
          onEditClick={() => onEdit(template.id)}
          onDeleteClick={() => onDelete(template.id)}
        />
      ))}
    </>
  );
};

export default UseTemplateModalBody;
